// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { Checkmark } from '@carbon/icons-react';
import {
  DataTableSkeleton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useRoles, useUsers } from '../../shared/users';
import type { RoleWithCanonical } from '../../shared/users';
import { roleLabel } from './orgRole';
import { permissionDescription, permissionTitle } from './permissionLabels';
import styles from './RolesPage.module.css';

interface PermissionRow {
  action: string;
  title: string;
  description: string;
}

function activeRoles(roles: RoleWithCanonical[]): RoleWithCanonical[] {
  return roles.filter(role => role.isActive !== false);
}

/** Union of every distinct `permissionAction` across the given roles, in
 *  first-seen order, each resolved to display copy. */
function buildPermissionRows(roles: RoleWithCanonical[]): PermissionRow[] {
  const seen = new Set<string>();
  const rows: PermissionRow[] = [];
  for (const role of roles) {
    for (const permission of role.permissions) {
      if (seen.has(permission.permissionAction)) continue;
      seen.add(permission.permissionAction);
      rows.push({
        action: permission.permissionAction,
        title: permissionTitle(permission.permissionAction),
        description: permissionDescription(permission.permissionAction),
      });
    }
  }
  return rows;
}

function roleDisplayLabel(role: RoleWithCanonical): string {
  return role.canonicalRole ? roleLabel(role.canonicalRole) : role.roleName;
}

interface GrantCellProps {
  granted: boolean;
  label: string;
}

function GrantCell({ granted, label }: GrantCellProps) {
  return (
    <span
      className={granted ? styles.grantOn : styles.grantOff}
      role="img"
      aria-label={granted ? `${label}: granted` : `${label}: not granted`}
    >
      {granted && <Checkmark size={12} />}
    </span>
  );
}

const SKELETON_HEADERS = [{ key: 'permission', header: 'Permission' }];

/**
 * Access > Roles: a read-only permission matrix across the tenant's actual
 * IAM roles, via `useRoles()` (`GET /api/v1/roles`, which already returns
 * each role's real `permissions: RolePermissionDTO[]` today). Columns are
 * whatever active roles the tenant has — not a hardcoded Owner/Admin/Member/
 * Viewer set — labeled with the Admin Center display name when the role maps
 * to one of the four canonical roles, or its raw IAM name otherwise (e.g. a
 * tenant-defined role). Rows are the union of every `permissionAction` seen
 * across those roles; `permissionLabels.ts` supplies human copy for it, since
 * no catalog of every possible action exists upstream.
 *
 * This is deliberately read-only. IAM exposes list (`GET /roles`) and create
 * (`POST /roles`) only — there is no `PUT`/`PATCH /roles/{id}` anywhere in its
 * contract (confirmed against both the vendored and upstream OpenAPI specs),
 * so a role's permissions can't be changed from here yet. An edit/save flow
 * would have nothing real to call; wiring one up would mean either faking
 * success or inventing a BFF endpoint IAM can't honor. See the PR description
 * for the follow-up this unblocks once IAM adds that capability.
 */
export default function RolesPage() {
  const { roles: rawRoles, isLoading: rolesLoading, error: rolesError } = useRoles();
  const { users, isLoading: usersLoading } = useUsers();

  const roles = useMemo(() => activeRoles(rawRoles), [rawRoles]);
  const permissionRows = useMemo(() => buildPermissionRows(roles), [roles]);
  const userCountByRole = useMemo(() => {
    const counts = new Map<string, number>();
    for (const role of roles) {
      counts.set(role.roleName, users.filter(u => u.roles.includes(role.roleName)).length);
    }
    return counts;
  }, [roles, users]);

  const isLoading = rolesLoading || usersLoading;

  return (
    <div>
      <div className={styles.title}>Roles</div>

      {rolesError && (
        <InlineNotification
          kind="error"
          lowContrast
          title={rolesError}
          hideCloseButton
          className={styles.notification}
        />
      )}

      {isLoading ? (
        <DataTableSkeleton
          headers={SKELETON_HEADERS}
          rowCount={8}
          showHeader={false}
          showToolbar={false}
          size="md"
        />
      ) : !rolesError && roles.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>No roles yet</div>
        </div>
      ) : (
        <Table className={styles.table}>
          <TableHead>
            <TableRow>
              <TableHeader className={styles.permissionHeader}>Permission</TableHeader>
              {roles.map(role => {
                const count = userCountByRole.get(role.roleName) ?? 0;
                return (
                  <TableHeader key={role.roleId} className={styles.roleHeader}>
                    <div className={styles.roleHeaderLabel}>{roleDisplayLabel(role)}</div>
                    <div className={styles.roleHeaderCount}>
                      {count} user{count === 1 ? '' : 's'}
                    </div>
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {permissionRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={roles.length + 1} className={styles.permissionCell}>
                  This tenant&apos;s roles don&apos;t define any permissions yet.
                </TableCell>
              </TableRow>
            ) : (
              permissionRows.map(permission => (
                <TableRow key={permission.action}>
                  <TableCell className={styles.permissionCell}>
                    <div className={styles.permissionTitle}>{permission.title}</div>
                    <div className={styles.permissionDescription}>{permission.description}</div>
                  </TableCell>
                  {roles.map(role => (
                    <TableCell key={role.roleId} className={styles.grantCell}>
                      <GrantCell
                        granted={role.permissions.some(
                          p => p.permissionAction === permission.action
                        )}
                        label={roleDisplayLabel(role)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
